import React, { useEffect, useState } from "react";
import { getRankHomeInfo, IRankHomeInfo } from "api/student/v2/Rank.api";
import { IUserStudent } from "api/interfaces/user.interface";
import { getImageUrl } from "helpers/Image.helper";
import { getCurrentLangName, getStudentNickname, getFlagByCountryCode, getStudentName } from "helpers/String.helper";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { usePopup } from "contexts/PopupContext";
import StudentProfilePopup from "../../popup/StudentProfilePopup";

const RankIndexComponent = () => {
    const { showPopup } = usePopup();
    const [rankData, setRankData] = useState<IRankHomeInfo | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // 랭킹 데이터 조회
    const fetchRankData = async () => {
        try {
            setIsLoading(true);
            const result: IRankHomeInfo = await getRankHomeInfo();
            setRankData(result);
        } catch (error) {
            console.error("Rank data fetch failed:", error);
            setRankData(null);
        } finally {
            setIsLoading(false);
        }
    };

    // 초기 데이터 로드
    useEffect(() => {
        fetchRankData();
    }, []);

    // 지망생 클릭 핸들러
    const handleStudentClick = (student: IUserStudent) => {
        showPopup(
            <StudentProfilePopup
                userStudentId={student.id}
                onClose={() => {
                    // Refresh rank data when popup closes
                    fetchRankData();
                }}
            />
        );
    };

    // 지망생 카드 컴포넌트 (검색화면 스타일)
    const StudentCard = ({ student, rank }: { student: IUserStudent; rank: number }) => {
        return (
            <div 
                className="flex-shrink-0 w-auto text-center px-2 cursor-pointer"
                onClick={() => handleStudentClick(student)}
            >
                <div className="relative inline-block mb-2">
                    {/* 랭킹 순위 배지 - 왼쪽 위 */}
                    <div className="absolute -top-1 -left-1 w-5 h-5 rounded-full flex items-center justify-center text-white font-bold text-xs z-10 bg-gray-500">
                        {rank}
                    </div>
                    
                    {/* 프로필 이미지 */}
                    <div className="w-16 h-16 sm:w-18 sm:h-18 rounded-full overflow-hidden mx-auto">
                        <img 
                            src={getImageUrl(student.profileImage, 200)} 
                            alt={getStudentNickname(student)} 
                            className="w-full h-full object-cover" 
                        />
                    </div>
                    
                    {/* 인증 뱃지 */}
                    {student.isVerified && (
                        <span className="absolute top-0 right-0">
                            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        </span>
                    )}
                    
                    {/* 국적 플래그 */}
                    <span className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 text-lg sm:text-xl">
                        {getFlagByCountryCode(student.country?.code)}
                    </span>
                </div>
                <p className="font-medium text-xs">{getStudentName(student)}</p>
            </div>
        );
    };

    return (
        <main className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-6 lg:pt-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtStudentRanking")}
                </h2>

                <div className="space-y-8">
                    {/* 로딩 상태 - 스켈레톤 */}
                    {isLoading && (
                        <>
                            {/* 캐스팅 매니저 조회수 랭킹 스켈레톤 */}
                            <section className="mb-6">
                                <div className="h-6 bg-gray-200 rounded w-48 mb-3 animate-pulse"></div>
                                <div className="flex md:grid overflow-x-auto scrollbar-hide md:grid-cols-3 lg:grid-cols-4 gap-2 py-2">
                                    {[...Array(8)].map((_, index) => (
                                        <div key={index} className="flex-shrink-0 w-auto text-center px-2">
                                            <div className="relative inline-block mb-2">
                                                {/* 랭킹 순위 배지 스켈레톤 */}
                                                <div className="absolute -top-1 -left-1 w-5 h-5 bg-gray-300 rounded-full animate-pulse"></div>
                                                
                                                {/* 프로필 이미지 스켈레톤 */}
                                                <div className="w-16 h-16 sm:w-18 sm:h-18 rounded-full bg-gray-200 animate-pulse"></div>
                                                
                                                {/* 인증 뱃지 스켈레톤 */}
                                                <div className="absolute top-0 right-0 w-3 h-3 bg-gray-300 rounded-full animate-pulse"></div>
                                                
                                                {/* 국적 플래그 스켈레톤 */}
                                                <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-6 h-4 bg-gray-200 rounded animate-pulse"></div>
                                            </div>
                                            <div className="h-3 bg-gray-200 rounded w-16 mx-auto animate-pulse"></div>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* 즐겨찾기 랭킹 스켈레톤 */}
                            <section>
                                <div className="h-6 bg-gray-200 rounded w-32 mb-4 animate-pulse"></div>
                                <div className="flex md:grid overflow-x-auto scrollbar-hide md:grid-cols-3 lg:grid-cols-4 gap-2 py-2">
                                    {[...Array(8)].map((_, index) => (
                                        <div key={index} className="flex-shrink-0 w-auto text-center px-2">
                                            <div className="relative inline-block mb-2">
                                                {/* 랭킹 순위 배지 스켈레톤 */}
                                                <div className="absolute -top-1 -left-1 w-5 h-5 bg-gray-300 rounded-full animate-pulse"></div>
                                                
                                                {/* 프로필 이미지 스켈레톤 */}
                                                <div className="w-16 h-16 sm:w-18 sm:h-18 rounded-full bg-gray-200 animate-pulse"></div>
                                                
                                                {/* 인증 뱃지 스켈레톤 */}
                                                <div className="absolute top-0 right-0 w-3 h-3 bg-gray-300 rounded-full animate-pulse"></div>
                                                
                                                {/* 국적 플래그 스켈레톤 */}
                                                <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-6 h-4 bg-gray-200 rounded animate-pulse"></div>
                                            </div>
                                            <div className="h-3 bg-gray-200 rounded w-16 mx-auto animate-pulse"></div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </>
                    )}

                    {/* 캐스팅 매니저 조회수 랭킹 */}
                    {!isLoading && rankData && (
                        <section className="mb-6">
                            <h2 className="text-lg font-semibold mb-3">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtCastingManagerViewRank")}
                            </h2>
                            {rankData.castingMangerViewCountRankList.length > 0 ? (
                                <div className="flex md:grid overflow-x-auto scrollbar-hide md:grid-cols-3 lg:grid-cols-4 gap-2 py-2">
                                    {rankData.castingMangerViewCountRankList.map((student, index) => (
                                        <StudentCard 
                                            key={student.id} 
                                            student={student} 
                                            rank={index + 1}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 bg-white rounded-2xl">
                                    <div className="text-gray-400 text-lg mb-2">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtNoViewRankData")}
                                    </div>
                                    <div className="text-gray-500 text-sm">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtNoViewRankDesc")}
                                    </div>
                                </div>
                            )}
                        </section>
                    )}

                    {/* 즐겨찾기 랭킹 */}
                    {!isLoading && rankData && (
                        <section>
                            <h2 className="text-lg font-semibold mb-4">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtFavoriteRank")}
                            </h2>
                            {rankData.favoriteCountRankList.length > 0 ? (
                                <div className="flex md:grid overflow-x-auto scrollbar-hide md:grid-cols-3 lg:grid-cols-4 gap-2 py-2">
                                    {rankData.favoriteCountRankList.map((student, index) => (
                                        <StudentCard 
                                            key={student.id} 
                                            student={student} 
                                            rank={index + 1}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 bg-white rounded-2xl">
                                    <div className="text-gray-400 text-lg mb-2">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtNoFavoriteRankData")}
                                    </div>
                                    <div className="text-gray-500 text-sm">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtNoFavoriteRankDesc")}
                                    </div>
                                </div>
                            )}
                        </section>
                    )}

                    {/* 데이터 로드 실패 */}
                    {!isLoading && !rankData && (
                        <div className="text-center py-12">
                            <div className="text-red-500 text-lg mb-2">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtDataLoadFailed")}
                            </div>
                            <button 
                                onClick={fetchRankData}
                                className="px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:opacity-90 transition-opacity"
                            >
                                {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtRetry")}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
};

export default RankIndexComponent;
